<svelte:options runes={false} />
<script>
  // Book tab (TASK-20260728-researcher-observability-ui): whole-book steering over time for cross-sectional
  // strategies — the stacked weight stream per rebalance hour, holdings count + gross exposure vs the frozen
  // risk limits, and the current book table (weight / vol / exit score per name). Sourced from the
  // book_snapshot lineage rows (one per rebalance period, captured even when nothing traded). Honest empty
  // state for strategies that never write book snapshots (single-symbol / non-versioned). Live wiring
  // mirrors CandlesSignalsTab (NAV-tick hint + 30 s fallback poll).
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { navTickSeq, decisionTickSeq } from '../../../lib/autotradeStore.js';
  import { formatNum } from '../../../lib/lineage.js';
  import { parseBookSeries, weightStream, latestBook } from '../../../lib/book.js';
  import BookWeightChart from '../../shared/charts/BookWeightChart.svelte';

  export let strategy = null;
  export let status = null;

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  let rows = [];
  let version = null;
  let loading = false;
  let error = null;
  let inFlight = false;

  async function loadAll() {
    if (!strategyId) return;
    if (inFlight) return;
    inFlight = true;
    loading = true;
    const at = $selectedOrgId;
    const sid = strategyId;
    try {
      const [book, v] = await Promise.all([
        api.getBookSnapshots(sid, {}, at),
        api.getLineageVersion(sid, at).catch(() => null),
      ]);
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return;
      rows = Array.isArray(book) ? book : [];
      version = v;
      error = null;
    } catch (err) {
      const msg = String(err?.message ?? err);
      error = msg.includes('status: 404')
        ? null
        : (msg.includes('details: ') ? msg.slice(msg.indexOf('details: ') + 'details: '.length) : msg);
    } finally {
      loading = false;
      inFlight = false;
    }
  }

  // (Strategy, org) reload — guard seeded from raw stores, not `$:` values (lesson 2026-07-14).
  let lastKey = '';
  $: {
    const key = `${strategy?.strategyId ?? ''}|${$selectedOrgId ?? ''}`;
    if (key !== lastKey && strategy?.strategyId) {
      lastKey = key;
      rows = [];
      loadAll();
    }
  }

  let lastSeq = 0;
  $: if (isRunning && $navTickSeq !== lastSeq) {
    lastSeq = $navTickSeq;
    loadAll();
  }

  // Refetch on a real decision watermark too (this task) — a book_snapshot lands as a decision row.
  let lastDecisionSeq = 0;
  $: if (isRunning && $decisionTickSeq !== lastDecisionSeq) {
    lastDecisionSeq = $decisionTickSeq;
    loadAll();
  }

  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(loadAll, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
  onMount(loadAll);

  $: series = parseBookSeries(rows);
  $: stream = weightStream(series, 5);
  $: current = latestBook(series);

  // Frozen risk-limit references from the version card (spec §8): per-name cap + max positions.
  $: params = version?.version?.params ?? null;
  $: capWeight = params?.sizing?.weight_cap ?? null;
  $: maxPositions = params?.entry?.max_positions ?? null;

  function fmtTime(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }
  function pct(v, digits = 1) {
    return v == null ? '—' : `${(Number(v) * 100).toFixed(digits)}%`;
  }
</script>

<div class="bk-root">
  <div class="bk-toolbar">
    {#if isRunning}<span class="bk-live" title="Updating live on each NAV tick">● live</span>{/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll} disabled={loading}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll}>Retry</button>
    </div>
  {:else if loading && series.length === 0}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if series.length === 0}
    <div class="empty-state">
      <p class="empty-state-description">
        <strong>No book snapshots for this run.</strong>
        Whole-book snapshots are captured once per rebalance period by cross-sectional versioned strategies
        (currently Shishito). Single-symbol strategies steer per-symbol — see Candles &amp; Signals and Decisions.
      </p>
    </div>
  {:else}
    <!-- Headline: the current book against its frozen limits. -->
    {#if current}
      <div class="bk-tiles">
        <div class="bk-tile">
          <span class="bk-tile-label">Holdings</span>
          <span class="bk-tile-value num">{current.count}{#if maxPositions != null}<span class="bk-limit"> / {maxPositions} max</span>{/if}</span>
          <span class="bk-tile-sub">as of {fmtTime(current.time)}</span>
        </div>
        <div class="bk-tile" class:bk-tile-warn={current.gross > 1}>
          <span class="bk-tile-label">Gross exposure</span>
          <span class="bk-tile-value num">{pct(current.gross)}</span>
          <span class="bk-tile-sub">excess over caps goes to cash (Option A) — gross must stay &lt; 100%</span>
        </div>
        <div class="bk-tile">
          <span class="bk-tile-label">Regime score</span>
          <span class="bk-tile-value num">{current.regime != null ? formatNum(current.regime, 2) : '—'}</span>
          <span class="bk-tile-sub">entry ≥ 0.45 · exit &lt; 0.15</span>
        </div>
      </div>
    {/if}

    <BookWeightChart {stream} {capWeight} />

    {#if current && current.holdings.length > 0}
      <div class="bk-current">
        <h4 class="bk-panel-title">Current book ({current.holdings.length} names, {fmtTime(current.time)})</h4>
        <div class="bk-current-scroll">
          <table>
            <thead>
              <tr><th>Symbol</th><th class="num">Weight</th><th class="num">Sizing vol</th><th class="num">Exit score</th><th class="num">Cap headroom</th></tr>
            </thead>
            <tbody>
              {#each current.holdings as h (h.symbol)}
                <tr>
                  <td>{h.symbol.replace('_', '-')}</td>
                  <td class="num">{pct(h.weight, 2)}</td>
                  <td class="num">{h.vol != null ? formatNum(h.vol, 4) : '—'}</td>
                  <td class="num">{h.exitScore != null ? formatNum(h.exitScore, 3) : '—'}</td>
                  <td class="num">{capWeight != null && h.weight != null ? pct(capWeight - h.weight, 2) : '—'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {:else if current}
      <div class="empty-state">
        <p class="empty-state-description">The book is currently empty (fully in cash) — the regime gate or exits have the strategy flat.</p>
      </div>
    {/if}
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .bk-root { display: flex; flex-direction: column; gap: 0.85rem; }
  .bk-toolbar { display: flex; align-items: center; gap: 0.75rem; }
  .bk-live { font-size: 0.75rem; color: var(--success, #1aa260); }
  .bk-panel-title { margin: 0 0 0.5rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  .num { font-variant-numeric: tabular-nums; }

  .bk-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 0.6rem; }
  .bk-tile {
    display: flex; flex-direction: column; gap: 0.15rem;
    border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.6rem 0.75rem;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
  }
  .bk-tile-warn { border-color: var(--danger, #e0533a); }
  .bk-tile-label { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  .bk-tile-value { font-size: 1.05rem; font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .bk-limit { font-size: 0.75rem; font-weight: 400; color: var(--text-muted, #8a8b8d); }
  .bk-tile-sub { font-size: 0.68rem; color: var(--text-muted, #8a8b8d); }

  .bk-current { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .bk-current-scroll { max-height: 380px; overflow: auto; }
  .bk-current table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  .bk-current th, .bk-current td { padding: 0.32rem 0.5rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; text-align: left; }
  .bk-current th { position: sticky; top: 0; background: var(--surface-1, #14151a); }
  .bk-current th.num, .bk-current td.num { text-align: right; }
</style>
